<script setup lang="ts">

import { useI18n } from 'vue-i18n'
const { t, locale } = useI18n()
import { useLogger } from 'vue-logger-plugin'
import { ref, computed, onMounted, watch } from 'vue'
import { setStoreLogger } from '../../logger.ts'
import { TextBox as KTextBox } from '@progress/kendo-vue-inputs'
import { DropDownList } from '@progress/kendo-vue-dropdowns';
import { GetCountriesFromLocalStorageOrApi, GetStatesFromLocalStorageOrApi } from '../../services/CountryCultureService'
import { getCountryLabelKey } from '@/config/countries'

const logger = useLogger()
setStoreLogger(logger)
logger.debug('AddressControl.vue')

interface Country {
  text: string
  id: string
}

interface State {
  text: string
  id: string
}

const selectedCountry = ref<Country>({ text: '', id: '' })
const selectedState = ref<State>({ text: '', id: '' })
const txtAddressName = ref<string>('')
const txtAddressLine1 = ref<string>('')
const txtAddressLine2 = ref<string>('')
const txtHouseNumber = ref<string>('')
const txtAddressCity = ref<string>('')
const txtAddressPostalCode = ref<string>('')
const countries= ref<Country[]>([])
const states = ref<State[]>([])
const nameValidationError = ref<string>('')
const streetValidationError = ref<string>('')
const houseNumberValidationError = ref<string>('')
const cityValidationError = ref<string>('')
const postalCodeValidationError = ref<string>('')
const countryValidationError = ref<string>('')

// Track which fields have been touched to show validation errors only after interaction
const touchedFields = ref<Set<string>>(new Set())

const props = withDefaults(defineProps<{
  name: string | null
  countryCode: string
  addressLine1: string | null
  addressLine2: string | null
  houseNumber: string | null
  city: string | null
  stateCode: string | null
  postalCode: string | null
  isdisabled?: boolean
  columnClass: string
  showfullnamefield?: boolean
  hidecountrypicker?: boolean
  isrequired?: boolean
}>(), {
  isdisabled: true,
  showfullnamefield: true,
  hidecountrypicker: false,
  isrequired: true
})

watch(
  () => props.name,
  (name) => {
    logger.debug("name:", name);
    if (name) {
      txtAddressName.value = name
    }
    else
    {
      txtAddressName.value = ''
    }
  },
  { immediate: true }
)
watch(
  () => props.addressLine1,
  (addressLine1) => {
    logger.debug("addressline1 watcher fired:", addressLine1);
    if (addressLine1) {
      txtAddressLine1.value = addressLine1
    }
    else
    {
      txtAddressLine1.value = ''
    }
  },
  { immediate: true }
)
watch(
  () => props.addressLine2,
  (addressLine2) => {
    logger.debug("addressline2:", addressLine2);
    if (addressLine2) {
      txtAddressLine2.value = addressLine2
    }
    else
    {
      txtAddressLine2.value = ''
    }
  },
  { immediate: true }
);
watch(
  () => props.houseNumber,
  (houseNumber) => {
    logger.debug("houseNumber:", houseNumber);
    if (houseNumber) {
      txtHouseNumber.value = houseNumber
    }
    else
    {
      txtHouseNumber.value = ''
    }
  },
  { immediate: true }
)
watch(
  () => props.city,
  (city) => {
    logger.debug("city watcher fired:", city);
    if (city) {
      txtAddressCity.value = city
    }
    else
    {
      txtAddressCity.value = ''
    }
  },
  { immediate: true }
);
watch(
  () => props.stateCode,
  (stateCode) => {
    logger.debug("stateCode:", stateCode);
    if (stateCode) {
      states.value.forEach((state) => {
        if (state.id == stateCode) {
          selectedState.value = state
        }
      })
    }
    else
    {
      selectedState.value = states.value[0]
    }
  },
  { immediate: true }
);
watch(
  () => props.postalCode,
  (postalCode) => {
    logger.debug("postalCode:", postalCode);
    if (postalCode && postalCode !== 'N/A') {
      txtAddressPostalCode.value = postalCode
    }
    else
    {
      txtAddressPostalCode.value = ''
    }
  },
  { immediate: true }
);
watch(
  () => props.countryCode,
  (countryCode) => {
    logger.debug("countryCode watcher fired:", countryCode);
    if (countryCode) {
      countries.value.forEach((country) => {
        if (country.id == countryCode) {
          selectedCountry.value = country
        }
      })
      countryChangeHandler(selectedCountry.value)
    }
  },
  { immediate: true }
);

const emit = defineEmits(["addressChange", "validation"]);

// Required postal code length per country
// FR/DE: 5 digits, AT: 4 digits, US: 5 (or 10 with the +4 suffix), CA: 7 (A1A 1A1)
const POSTAL_CODE_LENGTHS: Record<string, number> = {
  'FR': 5,
  'DE': 5,
  'AT': 4,
  'US': 5,
  'CA': 7,
};

// Countries that require a separate house number field
const HOUSE_NUMBER_COUNTRIES = ['FR', 'DE', 'AT'];

// Validation functions for European address formats
function validateStreetName(value: string): string {
  // TICKET REQUIREMENT: Allow ONLY letters (A-Z, a-z), EU accented letters (é, ö, ß), spaces, hyphens (-), apostrophes ('), periods (.), commas (,)
  // NO numbers, NO symbols (%, &, $, etc.)
  const sanitized = value.replace(/[^a-zA-Z\s\-',.\u00C0-\u017F]/g, '');
  return sanitized.substring(0, 100);
}

function validateStreetFormat(value: string): boolean {
  if (!value) return true;
  // NO numbers allowed in street name
  return /^[a-zA-Z\s\-',.\u00C0-\u017F]+$/.test(value);
}

function validateHouseNumber(value: string): string {
  // Allow alphanumeric characters, forward slashes, and hyphens (e.g. 12A, 12/3, 12-14)
  const sanitized = value.replace(/[^a-zA-Z0-9/\-]/g, '');
  return sanitized.substring(0, 10);
}

function validateHouseNumberFormat(value: string): boolean {
  if (!value) return true;
  // Digits followed by optional letter suffix, with optional "/" or "-" separators (e.g. 12A, 12/3, 12-14, 12A/3B)
  return /^[\da-zA-Z]+([/\-][\da-zA-Z]+)*$/.test(value);
}

function validateCityName(value: string): string {
  // TICKET REQUIREMENT: Allow ONLY letters (A-Z, a-z), EU accented letters, spaces, hyphens, periods (.), commas (,)
  // NO numbers, NO symbols
  const sanitized = value.replace(/[^a-zA-Z\s\-,.\u00C0-\u017F]/g, '');
  return sanitized.substring(0, 100);
}

function validatePostalCode(value: string, countryCode: string): string {
  switch (countryCode) {
    case 'FR': // France: 5 digits
      return value.replace(/[^0-9]/g, '').substring(0, 5);
    case 'DE': // Germany: 5 digits
      return value.replace(/[^0-9]/g, '').substring(0, 5);
    case 'AT': // Austria: 4 digits
      return value.replace(/[^0-9]/g, '').substring(0, 4);
    case 'US': // United States: 5 digits or 5+4 format (12345 or 12345-1234)
      return value.replace(/[^0-9\-]/g, '').replace(/^(\d{5})(\d{4})$/, '$1-$2').substring(0, 10);
    case 'CA': // Canada: A1A 1A1 format (letter-digit-letter space digit-letter-digit)
      return value.replace(/[^A-Za-z0-9\s]/g, '').toUpperCase().replace(/^([A-Z]\d[A-Z])(\d[A-Z]\d)$/, '$1 $2').substring(0, 7);
    default:
      return value;
  }
}

function validatePostalCodeFormat(value: string, countryCode: string): boolean {
  if (!value) return true;

  const requiredLength = POSTAL_CODE_LENGTHS[countryCode];
  if (!requiredLength) return true; // Unknown country, skip validation

  // For US, accept both 5 and 10 (with hyphen)
  if (countryCode === 'US') {
    return value.length === 5 || value.length === 10;
  }

  // For CA, check format (not just length)
  if (countryCode === 'CA') {
    return /^[A-Z]\d[A-Z]\s\d[A-Z]\d$/.test(value);
  }

  // For FR, DE, AT: check exact length
  return value.length === requiredLength;
}

// Input handlers for real-time validation
function handleStreetInput(...args: unknown[]) {
  touchedFields.value.add('street')
  const event = args[0] as { value: string };
  const validated = validateStreetName(event.value);
  if (validated !== event.value) {
    txtAddressLine1.value = validated;
  }
  if (!validateStreetFormat(txtAddressLine1.value)) {
    streetValidationError.value = t('validators.streetField', { fieldName: t('address.Street') });
  } else {
    streetValidationError.value = '';
  }
  addressChangeHandler();
}

function handleStreet2Input(...args: unknown[]) {
  const event = args[0] as { value: string };
  const validated = validateStreetName(event.value);
  if (validated !== event.value) {
    txtAddressLine2.value = validated;
  }
  addressChangeHandler();
}

function handleHouseNumberInput(...args: unknown[]) {
  touchedFields.value.add('houseNumber')
  const event = args[0] as { value: string };
  const validated = validateHouseNumber(event.value);
  if (validated !== event.value) {
    txtHouseNumber.value = validated;
  }
  if (txtHouseNumber.value && !validateHouseNumberFormat(txtHouseNumber.value)) {
    houseNumberValidationError.value = t('validators.houseNumberFormat', { fieldName: t('address.HouseNumber') });
  } else {
    houseNumberValidationError.value = '';
  }
  addressChangeHandler();
}

function handleCityInput(...args: unknown[]) {
  touchedFields.value.add('city')
  const event = args[0] as { value: string };
  const validated = validateCityName(event.value);
  if (validated !== event.value) {
    txtAddressCity.value = validated;
  }
  // Real-time validation for required field
  if (props.isrequired && !txtAddressCity.value?.trim()) {
    cityValidationError.value = t('validators.requiredField', { fieldName: t('address.City') });
  } else {
    cityValidationError.value = '';
  }
  addressChangeHandler();
}

function handlePostalCodeInput(...args: unknown[]) {
  touchedFields.value.add('postalCode')
  const event = args[0] as { value: string };
  const validated = validatePostalCode(event.value, selectedCountry.value.id);
  if (validated !== event.value) {
    txtAddressPostalCode.value = validated;
  }

  // Validate format after sanitization
  if (txtAddressPostalCode.value && !validatePostalCodeFormat(txtAddressPostalCode.value, selectedCountry.value.id)) {
    const required = POSTAL_CODE_LENGTHS[selectedCountry.value.id] || 0;
    postalCodeValidationError.value = t('validators.postalCodeLength', {
      fieldName: t('address.PostalCode'),
      requiredLength: required
    });
  } else {
    postalCodeValidationError.value = '';
  }

  addressChangeHandler();
}

onMounted(async () => {
  logger.debug('AddressControl onMounted - props:', {
    addressLine1: props.addressLine1,
    city: props.city,
    countryCode: props.countryCode,
    postalCode: props.postalCode
  })

  let selectedCountryName = ''

  GetCountriesFromLocalStorageOrApi().then((data) => {
    if (data?.items) {
      for (const country of data?.items) {
        const labelKey = getCountryLabelKey(country.countryCode)
        const translatedName = t(labelKey)
        countries.value.push({ text: translatedName, id: country.countryCode })

        if (country.countryCode == props.countryCode) {
          selectedCountryName = translatedName
          selectedCountry.value = {text: selectedCountryName, id: props.countryCode}
        }
      }
      countries.value.sort((a, b) => {
        return a.text.localeCompare(b.text)
      })
    }
  })

  if (props.countryCode.length > 0) {
    SetStateDropDownList(props.countryCode)
  }

  if (props.addressLine1) {
    txtAddressLine1.value = props.addressLine1
    if (props.addressLine2 != null)
    {
      txtAddressLine2.value = props.addressLine2
    }
    if (props.houseNumber != null)
    {
      txtHouseNumber.value = props.houseNumber
    }
    if (props.name != null)
    {
      txtAddressName.value = props.name
    }
    if (props.city != null)
    {
      txtAddressCity.value = props.city
    }
    if (props.postalCode != null && props.postalCode !== 'N/A')
    {
      txtAddressPostalCode.value = props.postalCode
    }
  }
})

function SetStateDropDownList(countryCode: string)
{
  if (countryCode.length > 0)
  {
    GetStatesFromLocalStorageOrApi(countryCode).then((data) => {
        if (data?.items) {
          for (const state of data?.items) {
            states.value.push({ text: state.state, id: state.stateCode })
          }
          states.value.sort((a, b) => {
            return a.text.localeCompare(b.text)
          })

          if (props.stateCode) {
            states.value.forEach((state) => {
              if (state.id == props.stateCode) {
                selectedState.value = state
              }
            })
          }
          else {
            selectedState.value = states.value[0]
          }
        }
      })
  }
}

function countryChangeHandler(country: Country) {
  logger.debug('countryChangeHandler')
  selectedCountry.value = country
  touchedFields.value.add('country')
  SetStateDropDownList(selectedCountry.value.id)

  // Validate postal code format when country changes (don't auto-truncate, just show error)
  if (txtAddressPostalCode.value) {
    // Check format and show error if invalid for the new country
    if (!validatePostalCodeFormat(txtAddressPostalCode.value, country.id)) {
      const required = POSTAL_CODE_LENGTHS[country.id] || 0;
      postalCodeValidationError.value = t('validators.postalCodeLength', {
        fieldName: t('address.PostalCode'),
        requiredLength: required
      });
    } else {
      postalCodeValidationError.value = '';
    }

    addressChangeHandler()
  }
}

function stateChangeHandler(state: State) {
  logger.debug('countryChangeHandler')
  selectedState.value = state
  addressChangeHandler()
}

// Error getters: return the message for an invalid field, '' when valid.
// They are pure reads of the field refs so both validate() (which writes the
// error refs) and the isValid computed (which must not) can share the rules.
function getNameError(): string {
  if (props.isrequired && (!txtAddressName.value || !txtAddressName.value.trim())) {
    return t('address.NameRequired')
  }
  if (txtAddressName.value) {
    const nameRegex = /^[\p{L}\s'-]+$/u;
    if (!nameRegex.test(txtAddressName.value)) {
      return t('validators.nameAddressField', { fieldName: t('address.Name') });
    }
    if (txtAddressName.value.trim().length < 2) {
      return t('validators.nameAddressMinLength', { fieldName: t('address.Name'), minimumLength: 2 });
    }
    if (txtAddressName.value.length > 50) {
      return t('validators.nameAddressMaxLength', { fieldName: t('address.Name'), maximumLength: 50 });
    }
  }
  return ''
}

function getStreetError(): string {
  if (!txtAddressLine1.value?.trim()) {
    return t('validators.requiredField', { fieldName: t('address.Street') })
  }
  if (!validateStreetFormat(txtAddressLine1.value)) {
    return t('validators.streetField', { fieldName: t('address.Street') })
  }
  return ''
}

function getHouseNumberError(): string {
  const requiresHouseNumber = HOUSE_NUMBER_COUNTRIES.includes(selectedCountry.value.id)
  if (requiresHouseNumber && !txtHouseNumber.value?.trim()) {
    return t('validators.requiredField', { fieldName: t('address.HouseNumber') })
  }
  if (txtHouseNumber.value && !validateHouseNumberFormat(txtHouseNumber.value)) {
    return t('validators.houseNumberFormat', { fieldName: t('address.HouseNumber') })
  }
  return ''
}

function getCityError(): string {
  if (!txtAddressCity.value?.trim()) {
    return t('validators.requiredField', { fieldName: t('address.City') })
  }
  return ''
}

function getPostalCodeError(): string {
  if (!txtAddressPostalCode.value?.trim()) {
    return t('validators.requiredField', { fieldName: t('address.PostalCode') })
  }
  if (!validatePostalCodeFormat(txtAddressPostalCode.value, selectedCountry.value.id)) {
    return t('validators.postalCodeLength', {
      fieldName: t('address.PostalCode'),
      requiredLength: POSTAL_CODE_LENGTHS[selectedCountry.value.id] || 0
    })
  }
  return ''
}

function getCountryError(): string {
  if (!selectedCountry.value.id?.trim()) {
    return t('validators.requiredField', { fieldName: t('address.Country') })
  }
  return ''
}

function validateName(): boolean {
  nameValidationError.value = getNameError()
  return !nameValidationError.value
}

function handleNameInput() {
  touchedFields.value.add('name')
  validateName()
  addressChangeHandler()
}

function handleNameChange() {
  validateName()
  addressChangeHandler()
}

// Reactive validity, recalculated on every keystroke so parents can gate a
// Save button on it (same contract as PhoneInputControl)
const isValid = computed(() => {
  if (!props.isrequired) return true

  return !getStreetError()
    && !getHouseNumberError()
    && !getCityError()
    && !getPostalCodeError()
    && !getCountryError()
    && !(props.showfullnamefield && getNameError())
})

watch(isValid, (valid) => emit('validation', valid), { immediate: true })

function validate(): boolean {
  if (!props.isrequired) return true

  // Mark all fields as touched when validating on submit
  touchedFields.value.add('name')
  touchedFields.value.add('street')
  touchedFields.value.add('houseNumber')
  touchedFields.value.add('city')
  touchedFields.value.add('postalCode')
  touchedFields.value.add('country')

  streetValidationError.value = getStreetError()
  houseNumberValidationError.value = getHouseNumberError()
  cityValidationError.value = getCityError()
  postalCodeValidationError.value = getPostalCodeError()
  countryValidationError.value = getCountryError()

  if (props.showfullnamefield) {
    validateName()
  }

  return isValid.value
}

defineExpose({ validate })

function addressChangeHandler()
{
  logger.debug('addressChangeHandler')

  // Split name on first space - everything before first space is firstName, rest is lastName
  let firstName = txtAddressName.value
  let lastName = ''

  if (txtAddressName.value) {
    const nameParts = txtAddressName.value.trim().split(' ')
    if (nameParts.length > 1) {
      firstName = nameParts[0]
      lastName = nameParts.slice(1).join(' ')
    }
  }

  emit('addressChange', {
    addressLine1: txtAddressLine1.value,
    addressLine2: txtAddressLine2.value,
    houseNumber: txtHouseNumber.value,
    city: txtAddressCity.value,
    stateCode: selectedCountry.value.id != 'US' && selectedCountry.value.id != 'CA' ? null : selectedState.value.id,
    postalCode: txtAddressPostalCode.value,
    countryCode: selectedCountry.value.id,
    firstName: firstName.trim(),
    lastName: lastName.trim()
  })
}

</script>

<template>
  <div>
    <div v-if="props.showfullnamefield" class="row mt-3">
      <div class="col-12 col-md-3">
        <template v-if="props.isrequired">* <span class="sr-only">{{ t('accessibility.required') }}</span></template> {{ t ('address.Name') }}:
      </div>
      <div :class="props.columnClass">
        <KTextBox
            v-model="txtAddressName"
            :rounded="'large'"
            :theme-color="'primary'"
            :disabled="props.isdisabled"
            :aria-required="props.isrequired ? 'true' : undefined"
            @input="handleNameInput"
            @change="handleNameChange"
            @blur="validateName"
          ></KTextBox>
        <div v-if="nameValidationError && touchedFields.has('name')" class="k-form-error">{{ nameValidationError }}</div>
      </div>
    </div>
    <div v-if="!props.hidecountrypicker" class="row mt-3">
      <div class="col-12 col-md-3">
        * <span class="sr-only">{{ t('accessibility.required') }}</span> {{ t ('address.Country') }}:
      </div>
      <div :class="props.columnClass">
        <DropDownList
          :data-items='countries'
          :value= "selectedCountry"
          :text-field="'text'"
          :data-item-key="'id'"
          :disabled="props.isdisabled"
          aria-required="true"
          @change="countryChangeHandler($event.target.value)">
        </DropDownList>
        <div v-if="countryValidationError && touchedFields.has('country')" class="k-form-error">{{ countryValidationError }}</div>
      </div>
    </div>
    <div v-if="selectedCountry.id == 'US' || selectedCountry.id == 'CA'">
      <div class="row mt-3">
        <div class="col-12 col-md-3">
          <template v-if="props.isrequired">* <span class="sr-only">{{ t('accessibility.required') }}</span></template> {{ t ('address.AddressLines') }}:
        </div>
        <div :class="props.columnClass">
          <KTextBox
            v-model="txtAddressLine1"
            :rounded="'large'"
            :theme-color="'primary'"
            :disabled="props.isdisabled"
            :aria-required="props.isrequired ? 'true' : undefined"
            @input="handleStreetInput"
            @change="addressChangeHandler"
          ></KTextBox>
          <div v-if="streetValidationError && touchedFields.has('street')" class="k-form-error">{{ streetValidationError }}</div>
        </div>
      </div>
      <div class="row mt-2">
        <div class="col-12 col-md-3">
        </div>
        <div :class="props.columnClass">
          <KTextBox
            v-model="txtAddressLine2"
            :rounded="'large'"
            :theme-color="'primary'"
            :disabled="props.isdisabled"
            @input="handleStreet2Input"
            @change="addressChangeHandler"
          ></KTextBox>
        </div>
      </div>
      <div class="row mt-2">
        <div class="col-12 col-md-3">
          <template v-if="props.isrequired">* <span class="sr-only">{{ t('accessibility.required') }}</span></template> {{ t ('address.City') }}:
        </div>
        <div :class="props.columnClass">
          <KTextBox
            v-model="txtAddressCity"
            :rounded="'large'"
            :theme-color="'primary'"
            :disabled="props.isdisabled"
            :aria-required="props.isrequired ? 'true' : undefined"
            @input="handleCityInput"
            @change="addressChangeHandler"
          ></KTextBox>
          <div v-if="cityValidationError && touchedFields.has('city')" class="k-form-error">{{ cityValidationError }}</div>
        </div>
      </div>
      <div class="row mt-2">
        <div class="col-12 col-md-3">
          <template v-if="props.isrequired">* <span class="sr-only">{{ t('accessibility.required') }}</span></template> {{ t ('address.State') }}:
        </div>
        <div :class="props.columnClass">
          <DropDownList
            :data-items='states'
            :value= "selectedState"
            :text-field="'text'"
            :disabled="props.isdisabled"
            :data-item-key="'id'"
            :aria-required="props.isrequired ? 'true' : undefined"
            @change="stateChangeHandler($event.target.value)">
          </DropDownList>
        </div>
      </div>
      <div class="row mt-2">
        <div class="col-12 col-md-3">
          <template v-if="props.isrequired">* <span class="sr-only">{{ t('accessibility.required') }}</span></template> {{ t ('address.PostalCode') }}:
        </div>
        <div :class="props.columnClass">
          <KTextBox
            v-model="txtAddressPostalCode"
            :rounded="'large'"
            :disabled="props.isdisabled"
            :theme-color="'primary'"
            :aria-required="props.isrequired ? 'true' : undefined"
            @input="handlePostalCodeInput"
            @change="addressChangeHandler"
          ></KTextBox>
          <div v-if="postalCodeValidationError && touchedFields.has('postalCode')" class="k-form-error">{{ postalCodeValidationError }}</div>
        </div>
      </div>
    </div>
    <!-- France, Austria and Germany address template -->
    <div v-if="selectedCountry.id == 'FR' || selectedCountry.id == 'AT' || selectedCountry.id == 'DE'">
      <div class="row mt-3">
        <div class="col-12 col-md-3">
          <template v-if="props.isrequired">* <span class="sr-only">{{ t('accessibility.required') }}</span></template> {{ t ('address.Street') }}:
        </div>
        <div :class="props.columnClass">
          <KTextBox
            v-model="txtAddressLine1"
            :rounded="'large'"
            :theme-color="'primary'"
            :disabled="props.isdisabled"
            :aria-required="props.isrequired ? 'true' : undefined"
            @input="handleStreetInput"
            @change="addressChangeHandler"
          ></KTextBox>
          <div v-if="streetValidationError && touchedFields.has('street')" class="k-form-error">{{ streetValidationError }}</div>
        </div>
      </div>
      <div class="row mt-2">
        <div class="col-12 col-md-3">
          <template v-if="props.isrequired">* <span class="sr-only">{{ t('accessibility.required') }}</span></template> {{ t ('address.HouseNumber') }}:
        </div>
        <div :class="props.columnClass">
          <KTextBox
            v-model="txtHouseNumber"
            :rounded="'large'"
            :theme-color="'primary'"
            :disabled="props.isdisabled"
            :aria-required="props.isrequired ? 'true' : undefined"
            @input="handleHouseNumberInput"
            @change="addressChangeHandler"
          ></KTextBox>
          <div v-if="houseNumberValidationError && touchedFields.has('houseNumber')" class="k-form-error">{{ houseNumberValidationError }}</div>
        </div>
      </div>
      <div v-if="selectedCountry.id != 'AT'" class="row mt-3">
        <div class="col-12 col-md-3 address-street2-label">
          {{ t ('address.Street2') }}:
        </div>
        <div :class="props.columnClass">
          <KTextBox
            v-model="txtAddressLine2"
            :rounded="'large'"
            :theme-color="'primary'"
            :disabled="props.isdisabled"
            @input="handleStreet2Input"
            @change="addressChangeHandler"
          ></KTextBox>
        </div>
      </div>
      <div class="row mt-2">
        <div class="col-12 col-md-3">
          <template v-if="props.isrequired">* <span class="sr-only">{{ t('accessibility.required') }}</span></template> {{ t ('address.PostalCode') }}:
        </div>
        <div :class="props.columnClass">
          <KTextBox
            v-model="txtAddressPostalCode"
            :rounded="'large'"
            :disabled="props.isdisabled"
            :theme-color="'primary'"
            :aria-required="props.isrequired ? 'true' : undefined"
            @input="handlePostalCodeInput"
            @change="addressChangeHandler"
          ></KTextBox>
          <div v-if="postalCodeValidationError && touchedFields.has('postalCode')" class="k-form-error">{{ postalCodeValidationError }}</div>
        </div>
      </div>
      <div class="row mt-2">
        <div class="col-12 col-md-3">
          <template v-if="props.isrequired">* <span class="sr-only">{{ t('accessibility.required') }}</span></template> {{ t ('address.City') }}:
        </div>
        <div :class="props.columnClass">
          <KTextBox
            v-model="txtAddressCity"
            :rounded="'large'"
            :theme-color="'primary'"
            :disabled="props.isdisabled"
            :aria-required="props.isrequired ? 'true' : undefined"
            @input="handleCityInput"
            @change="addressChangeHandler"
          ></KTextBox>
          <div v-if="cityValidationError && touchedFields.has('city')" class="k-form-error">{{ cityValidationError }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
</style>
